import { instantOf } from '../freshness.js'
import { ageSeconds, ageText, dayLabel } from './time.js'

// What the Tonight view says about a card, derived from the card and the clock. P6-328b (GN-1b).
//
// Pure, and it takes `nowMs` as an argument everywhere: a test asserts the wording
// of every state without waiting for one, and the whole page renders ONE "now"
// (`poll.js::useClock`) so the age, the stale rule and "tips in N min" cannot
// disagree with each other by a tick.

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 STALE IS A PROPERTY OF THE CLOCK AND THE STAMP, NEVER OF A STATUS STRING
// ═══════════════════════════════════════════════════════════════════════════
//
// A live card whose bot-side stamp (`as_of`: the OLDEST fetch among the files the
// card was built from) is 120 seconds old is a card nobody has heard anything
// about for two minutes. It is drawn as what the page last knew and when it
// stopped, with no error text and no chip (HP5, and the presence handoff's
// unreachable rule): the page is not allowed to say a feed is broken, only how old
// what it knows is.
//
// ⚠️ IT IS `as_of`, NOT "THE CONTENT HAS NOT CHANGED". Halftime changes nothing
// for fifteen minutes and the bot rewrites the live object on every fetch so the
// stamp keeps moving (`utils/hoops_projection.py` on quiet versus stale): a
// quiet game must not read as a dead feed. Only a fetch that FAILED stops the
// stamp, and that is what this rule is waiting for.
//
// ⚠️ IT IS ALSO THE PAGE'S OWN READ. If this page stops reading the board (the
// tab was asleep, the network dropped) its copy ages exactly as a dead feed's
// would, and the card goes stale for the same honest reason: that is what the
// page last knew. `Once a read has succeeded in the session, keep showing the
// last copy with its age.`

/** Two minutes. The design's `staleAfter` default; "120 s of no change" in the brief. */
export const STALE_AFTER_MS = 120_000

/** `NBA` for both of the NBA's own feeds (`cdn` and `origin` are two hosts of one
 * publisher), and whatever else a future source calls itself, upper-cased. */
const SOURCE_LABELS = Object.freeze({ cdn: 'NBA', origin: 'NBA' })

export function sourceLabel(source) {
  if (typeof source !== 'string' || source === '') return null
  return Object.hasOwn(SOURCE_LABELS, source) ? SOURCE_LABELS[source] : source.toUpperCase()
}

/**
 * `NBA · 14s`: where it came from and how old it is.
 *
 * ⚠️ AN UNREADABLE STAMP IS SAID IN WORDS, never printed as `0s` or hidden: a label that
 * claims a freshness it does not have is the failure this whole line exists to prevent
 * (`freshness.js`'s rule for a missing date).
 */
export function sourceAge(source, stampMs, nowMs) {
  const name = sourceLabel(source)
  const age = stampMs === null ? 'age unknown' : ageText(ageSeconds(stampMs, nowMs))
  return name === null ? age : `${name} · ${age}`
}

/**
 * Which of the four states a card is in, and how old it is.
 *
 * `kind` is `pregame`, `live`, `stale` or `final`: the bot's three statuses, plus
 * `stale` for a live one whose stamp has run past `STALE_AFTER_MS`. Only a LIVE card can
 * be stale — a pregame card's stamp is the time of the last change on a quiet board and
 * can be hours old without anything being wrong, and a final card never changes again.
 */
export function cardKind(card, nowMs) {
  const stampMs = instantOf(card.as_of)
  const ageMs = stampMs === null ? null : Math.max(0, nowMs - stampMs)
  const stale = card.status === 'live' && ageMs !== null && ageMs >= STALE_AFTER_MS
  return { kind: stale ? 'stale' : card.status, stampMs, ageMs }
}

/** `Q1`..`Q4`, `OT`, `2OT`, `3OT`; `null` for anything that is not a period. */
export function periodName(period) {
  if (!Number.isInteger(period) || period < 1) return null
  if (period <= 4) return `Q${period}`
  return period === 5 ? 'OT' : `${period - 4}OT`
}

/**
 * Is a live game between periods?
 *
 * 🔴 THE CLOCK, NOT THE WORDS. `period` and `clock` are read; `status_text` is not, and
 * must not be: its wording is the box score's (`Q1 6:56`) and was unobserved at halftime,
 * between quarters and in overtime, so matching on it would be a guess about three
 * states nobody has seen. The rule is the manager's: a live game whose clock reads
 * `0:00`, or reads nothing (the feed's blank between quarters, which the producer
 * publishes as `null`).
 */
export function endOfPeriod(card) {
  return card.status === 'live' && (card.clock === null || card.clock === '0:00')
}

function liveText(card) {
  const name = periodName(card.period)
  if (name === null) return 'Live'
  if (endOfPeriod(card)) return card.period === 2 ? 'Halftime' : `End of ${name}`
  return typeof card.clock === 'string' && card.clock !== '' ? `${name} ${card.clock}` : name
}

/**
 * The status line on a card: what stage the game is at.
 *
 * `Tips in 5 min` · `Q3 4:12` · `Halftime` · `End of Q1` · `Last knew · Q1 1:48` ·
 * `Final` · `Final · OT`. Built from `period` and `clock` and the clock, never parsed
 * out of `status_text`.
 */
export function statusText(card, kind, nowMs) {
  if (kind === 'pregame') {
    const tip = instantOf(card.tip_utc)
    if (tip === null) return 'Not started'
    const minutes = Math.round((tip - nowMs) / 60_000)
    // Past its listed tip and still pregame: the box score has not said the game has begun.
    // MIA @ TOR was listed at 23:00 and under way at 23:13, so this is a real state and it
    // is not "tips in 0 min", which would be a claim about the next sixty seconds.
    if (minutes <= 0) return 'Tip-off pending'
    return minutes >= 60 ? `Tips in ${Math.floor(minutes / 60)}h ${minutes % 60}m` : `Tips in ${minutes} min`
  }
  if (kind === 'final') {
    const name = card.period > 4 ? periodName(card.period) : null
    return name === null ? 'Final' : `Final · ${name}`
  }
  if (kind === 'stale') return `Last knew · ${liveText(card)}`
  return liveText(card)
}

/** `3-2` as `3–2`, the way the design sets a record. Anything else is returned as it came. */
export function recordText(record) {
  return typeof record === 'string' && /^\d+-\d+$/.test(record) ? record.replace('-', '–') : record ?? null
}

/** Everything after the first word, as the design shortens a name on a card: `Brunson`, `Towns`. */
export function surname(name) {
  if (typeof name !== 'string') return ''
  const parts = name.trim().split(/\s+/)
  return parts.length > 1 ? parts.slice(1).join(' ') : parts[0] ?? ''
}

/** The words for a count the headline spells out: `four`, and `9` past eight. */
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight']
const spell = (count) => WORDS[count] ?? String(count)

/**
 * The sentence at the top of the page: `Four live, one still to tip, one final.`
 *
 * Counts by the state the card is DRAWN in, so a stale card is counted as live (it is a live
 * game the page has lost touch with, not a different kind of game) and the headline cannot
 * disagree with the cards under it.
 */
export function headline(kinds) {
  const live = kinds.filter((kind) => kind === 'live' || kind === 'stale').length
  const pregame = kinds.filter((kind) => kind === 'pregame').length
  const final = kinds.filter((kind) => kind === 'final').length
  const parts = [
    [live, 'live'],
    [pregame, 'still to tip'],
    [final, 'final'],
  ]
    .filter(([count]) => count > 0)
    .map(([count, label]) => `${spell(count)} ${label}`)
  if (parts.length === 0) return 'No games tonight.'
  const sentence = parts.join(', ')
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`
}

const RANK = Object.freeze({ live: 0, stale: 1, pregame: 2, final: 3 })

/**
 * The board in the order it is drawn: live, then stale, then still to tip, then final, and by
 * tip time within each. A tip nobody can read sorts last in its group, and the sort is stable,
 * so two cards that tie keep the order the bot published them in.
 */
export function orderedCards(cards, nowMs) {
  return cards
    .map((card, index) => ({ card, index, ...cardKind(card, nowMs) }))
    .sort((a, b) => {
      const rank = RANK[a.kind] - RANK[b.kind]
      if (rank !== 0) return rank
      const tipA = instantOf(a.card.tip_utc) ?? Infinity
      const tipB = instantOf(b.card.tip_utc) ?? Infinity
      if (tipA !== tipB) return tipA < tipB ? -1 : 1
      return a.index - b.index
    })
}

/**
 * The TV line: the broadcasters whose `media` is `tv`, in the order the bot listed them
 * (national first), or `null` when there is nothing honest to say.
 *
 * ⚠️ THREE STATES, AND ONLY ONE IS A LIST. `null` (or absent) is "the schedule has not been
 * read for this board": the page does not know, so it says nothing. `[]` is "it was read and
 * lists none". A list with no `tv` in it (radio and streaming only) says nothing either: a
 * `TV` row reading "none listed" for a game that is on the radio would be a claim, and the
 * radio is not what the row is for.
 */
export function tvText(card) {
  if (!Array.isArray(card.broadcasters)) return null
  const names = []
  for (const entry of card.broadcasters) {
    if (entry?.media !== 'tv' || typeof entry.name !== 'string' || entry.name === '') continue
    if (!names.includes(entry.name)) names.push(entry.name)
  }
  return names.length === 0 ? null : names.join(' · ')
}

/**
 * A side's sub-line on a card.
 *
 * Live: `5–2 · 7 TO · Bonus` (the record, the timeouts LEFT, and the bonus when it is on).
 * Otherwise `New York · 5–2`. A figure the bot says nothing about (`null` timeouts before the
 * tip) is left out, never zeroed: a count is a claim and an absence is not (P6-156).
 */
export function teamSub(side, kind) {
  const record = recordText(side.record)
  if (kind === 'live' || kind === 'stale') {
    return [
      record,
      Number.isInteger(side.timeouts) ? `${side.timeouts} TO` : null,
      side.in_bonus === true ? 'Bonus' : null,
    ]
      .filter(Boolean)
      .join(' · ')
  }
  return [side.city, record].filter(Boolean).join(' · ')
}

/**
 * A side's leader on a final card: the surname and the three figures the bot computed.
 * `null` until somebody has scored (`leader` is `null`), which is what a card with no
 * leader looks like and is not drawn as a name with zeros.
 */
export function leaderText(leader) {
  if (!leader || typeof leader !== 'object') return null
  const figures = [
    [leader.points, 'PTS'],
    [leader.rebounds, 'REB'],
    [leader.assists, 'AST'],
  ]
    .filter(([value]) => Number.isInteger(value))
    .map(([value, unit]) => `${value} ${unit}`)
  const name = surname(leader.name)
  if (name === '' && figures.length === 0) return null
  return { name, figures: figures.join(' · ') }
}

const LEADER_STATS = Object.freeze([
  ['points', 'PTS'],
  ['rebounds', 'REB'],
  ['assists', 'AST'],
])

/**
 * A FINAL card's three leaders for one side, in the design's order (points, rebounds, assists): `[{ key, name, figure }]`,
 * the name a surname and the figure `35 PTS`. P6-343.
 *
 * The producer's `leaders` is an object with one entry per stat somebody has a figure in, so a stat it left out is left out
 * here: no name beside a zero, and no empty cell. `[]` for a side with none, and for anything that is not the shape.
 */
export function leadersText(side) {
  const found = side?.leaders
  if (found === null || typeof found !== 'object') return []
  return LEADER_STATS.flatMap(([key, unit]) => {
    const entry = found[key]
    if (entry === null || typeof entry !== 'object' || !Number.isInteger(entry.value)) return []
    const name = surname(entry.name)
    return name === '' ? [] : [{ key, name, figure: `${entry.value} ${unit}` }]
  })
}

/**
 * What a final card's leaders row says for one side: the three (`leadersText`), else the ONE top scorer the producer has
 * always published (`leaderText`), as a single cell with his three figures, else nothing. The fallback is for a response
 * cached from, or a bot from, before the three existed: deploy order is not ours to choose.
 */
export function sideLeaders(side) {
  const three = leadersText(side)
  if (three.length > 0) return three
  const one = leaderText(side?.leader)
  return one === null ? [] : [{ key: 'leader', name: one.name, figure: one.figures }]
}

/**
 * A pregame card's *Last met* line, the design's own words: `BOS 119–104 NYK · 11 Apr 2026, at NYK`, the winner first.
 * `null` when the card has no `last_met` or it is not readable, and then there is NO row (the design's missing-parts rule:
 * *no last meeting means no row*), never a placeholder. P6-343.
 *
 * `home` is `null` at a neutral site, and then the `, at X` is left off rather than guessed. The date is the producer's
 * Eastern date and is printed as a DATE (`dayLabel` names it, noon UTC, so no zone moves it); the weekday is dropped, as the
 * design's own line does, and the year is the date's own.
 */
export function lastMetText(met) {
  if (met === null || typeof met !== 'object') return null
  const { winner, loser, winner_score: winnerScore, loser_score: loserScore, date, home } = met
  if (typeof winner !== 'string' || winner === '' || typeof loser !== 'string' || loser === '') return null
  if (!Number.isInteger(winnerScore) || !Number.isInteger(loserScore)) return null
  const day = dayLabel(date)
  if (day === null) return null
  const where = typeof home === 'string' && home !== '' ? `, at ${home}` : ''
  return `${winner} ${winnerScore}–${loserScore} ${loser} · ${day.replace(/^\S+ /, '')} ${date.slice(0, 4)}${where}`
}

/**
 * The empty night's line under the day: `9 games · first tip 12:00 PM EST`, or `First tip 12:00 PM EST` when the producer
 * did not say how many (a record from before it did). `tip` is the already-formatted time and zone. P6-343.
 */
export function nextLine(count, tip) {
  if (!Number.isInteger(count) || count < 1) return `First tip ${tip}`
  return `${count} ${count === 1 ? 'game' : 'games'} · first tip ${tip}`
}
